import { AlertTriangle, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader } from '~/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type {
  FunnelOutcome,
  FunnelProductsQuery,
  FunnelStage,
} from '../../types/batch-funnel.api.types';
import {
  FUNNEL_TONE_CHIP,
  FUNNEL_TONE_FILL,
  outcomeBucketTone,
  outcomeDrillQuery,
} from '../../utils/batch-funnel';

interface FunnelOutcomesProps {
  outcomes: FunnelOutcome[];
  outcomesCheck: boolean | null;
  stages: FunnelStage[];
  onDrill: (title: string, query: FunnelProductsQuery) => void;
}

/**
 * "Where every product ended up" — one stacked bar plus a table over
 * `outcomes[]`, which always partition the Received count, so the page never
 * loses products silently. EXCLUDED is by design and not flagged as a loss.
 */
export function FunnelOutcomes({ outcomes, outcomesCheck, stages, onDrill }: FunnelOutcomesProps) {
  const { t } = useTranslation('catalog-operations');
  const visible = outcomes.filter((o) => o.count > 0);
  // The bucket counts partition Received (§4.7) — their sum is the bar's 100%.
  const total = visible.reduce((sum, o) => sum + o.count, 0);
  const stageLabel = (key: string | null) => stages.find((s) => s.key === key)?.label ?? key ?? '—';

  if (visible.length === 0) return null;

  return (
    <Card data-testid="batch-funnel-outcomes-card">
      <CardHeader className="p-6 pb-3">
        <h2 className="text-xl" data-testid="batch-funnel-outcomes-title">
          {t('batchFunnel.outcomes.title')}
        </h2>
        <p
          className="text-muted-foreground text-sm font-light"
          data-testid="batch-funnel-outcomes-total"
        >
          {t('batchFunnel.outcomes.subtitle', { total: total.toLocaleString() })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4 px-6 pb-6">
        {outcomesCheck === false && (
          <p
            className="text-warning flex items-center gap-1.5 text-sm"
            data-testid="batch-funnel-outcomes-check-failed-banner"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('batchFunnel.outcomes.checkFailed')}
          </p>
        )}

        <div
          className="bg-muted flex h-3 w-full overflow-hidden rounded-full"
          role="img"
          aria-label={t('batchFunnel.outcomes.barLabel')}
          data-testid="batch-funnel-outcomes-bar"
        >
          {visible.map((o, i) => (
            <Tooltip
              key={`${o.bucket ?? ''}:${o.stage ?? ''}:${o.code ?? ''}:${String(i)}`}
              delayDuration={100}
            >
              <TooltipTrigger asChild>
                <span
                  className={cn('h-full', FUNNEL_TONE_FILL[outcomeBucketTone(o.bucket)])}
                  style={{ width: `${String((o.count / total) * 100)}%` }}
                />
              </TooltipTrigger>
              <TooltipContent>
                {o.label ?? o.bucket} · {o.count.toLocaleString()}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        <Table data-testid="batch-funnel-outcomes-table">
          <TableHeader>
            <TableRow>
              <TableHead>{t('batchFunnel.outcomes.colOutcome')}</TableHead>
              <TableHead>{t('batchFunnel.outcomes.colStage')}</TableHead>
              <TableHead className="text-right">{t('batchFunnel.outcomes.colProducts')}</TableHead>
              <TableHead className="text-right">{t('batchFunnel.outcomes.colShare')}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((o, i) => {
              const query = outcomeDrillQuery(o);
              const title = o.label ?? o.bucket ?? '';
              const rowKey = [o.bucket, o.stage, o.code].filter(Boolean).join('-') || String(i);
              return (
                <TableRow
                  key={`${o.bucket ?? ''}:${o.stage ?? ''}:${o.code ?? ''}:${String(i)}`}
                  data-testid={`batch-funnel-outcomes-row-${rowKey}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'h-2.5 w-2.5 shrink-0 rounded-full',
                          FUNNEL_TONE_FILL[outcomeBucketTone(o.bucket)],
                        )}
                        aria-hidden="true"
                      />
                      <span className="text-foreground">{title}</span>
                      {o.loss && (
                        <span
                          data-testid={`batch-funnel-outcomes-loss-badge-${rowKey}`}
                          className={cn(
                            'rounded px-1.5 py-0.5 text-[11px]',
                            FUNNEL_TONE_CHIP.error,
                          )}
                        >
                          {t('batchFunnel.outcomes.loss')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{stageLabel(o.stage)}</TableCell>
                  <TableCell
                    className="text-foreground text-right font-medium tabular-nums"
                    data-testid={`batch-funnel-outcomes-count-value-${rowKey}`}
                  >
                    {o.count.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-right tabular-nums">
                    {`${((o.count / total) * 100).toFixed(1)}%`}
                  </TableCell>
                  <TableCell className="py-1 text-right">
                    {query && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 cursor-pointer"
                        aria-label={t('batchFunnel.viewProducts')}
                        data-testid={`batch-funnel-outcomes-view-products-button-${rowKey}`}
                        onClick={() => {
                          onDrill(title, query);
                        }}
                      >
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
